import axios, { AxiosError } from 'axios';
import LoginInvalidError from '@/domain/errors/LoginInvalidError';
import UnauthorizedError from '@/domain/errors/UnauthorizedError';
import NotFoundError from '@/domain/errors/NotFoundError';
import UnknownError from '@/domain/errors/UnknownError';
import EmailCodeMismatch from '@/domain/errors/EmailCodeMismatch';
import EmailInUseError from '@/domain/errors/EmailInUseError';
import EmailNotConfirmedError from '@/domain/errors/EmailNotConfirmed';
import EmailAlreadyConfirmed from '@/domain/errors/EmailAlreadyConfirmed';
import EmailConfirmationCooldown from '@/domain/errors/EmailConfirmationCooldown';
import EmailConfirmationExpired from '@/domain/errors/EmailConfirmationExpired';
import TokenExpiredError from '@/domain/errors/TokenExpiredError';
import EmailCodeConfirmationInvalid from '@/domain/errors/EmailCodeConfirmationInvalid';
import router from '@/router';
import { useAuthStore } from '@/store/auth.store';
import MaxAttemptsReach from "@/domain/errors/MaxAttemptsReach";
import TwoStepRequiredError from "@/domain/errors/TwoStepRequiredError";
import AwaitRetryError from "@/domain/errors/AwaitRetryError";

export type ErrorTypes =
  | 'LOGIN_INVALID_ERROR'
  | 'UNAUTHORIZED_ERROR'
  | 'TOKEN_EXPIRED_ERROR'
  | 'TOKEN_INVALID_ERROR'
  | 'INVALID_TOKEN'
  | 'FORBIDDEN_ERROR'
  | 'INVALID_PASSWORD_ERROR'
  | 'EMAIL_SEND_ERROR'
  | 'EMAIL_CODE_MISMATCH'
  | 'EMAIL_CODE_CONFIRMATION_INVALID'
  | 'EMAIL_IN_USE_ERROR'
  | 'EMAIL_NOT_CONFIRMED'
  | 'EMAIL_ALREADY_CONFIRMED'
  | 'EMAIL_CONFIRMATION_COOLDOWN'
  | 'EMAIL_CONFIRMATION_EXPIRED'
  | 'CODE_INVALID_ERROR'
  | 'CODE_EXPIRED_ERROR'
  | 'NOT_FOUND_ERROR'
  | 'HTTP_CLIENT_ERROR'
  | 'FORM_ERROR'
  | 'JSON_MALFORMED_ERROR'
  | 'SEND_SMS_ERROR'
  | 'DATABASE_ERROR'
  | 'UNDER_MAINTENANCE_ERROR'
  | 'BACK_ACCOUNT_ERROR'
  | 'MAX_ATTEMPTS_REACH'
  | 'TWO_STEP_REQUIRED'
  | 'WISH_FC_SHOP_IDS_ERROR'
  | 'GENERATE_TEMPLATE_ERROR'
  | 'GET_TEMPLATE_ERROR'
  | 'AWAIT_RETRY_ERROR'
  | 'UNKNOWN_ERROR';

const ErrorMap: Record<ErrorTypes, new (message: string, data?: unknown) => Error> = {
  LOGIN_INVALID_ERROR: LoginInvalidError,
  UNAUTHORIZED_ERROR: UnauthorizedError,
  UNKNOWN_ERROR: UnknownError,
  NOT_FOUND_ERROR: NotFoundError,
  EMAIL_NOT_CONFIRMED: EmailNotConfirmedError,
  EMAIL_CODE_MISMATCH: EmailCodeMismatch,
  EMAIL_IN_USE_ERROR: EmailInUseError,
  EMAIL_CODE_CONFIRMATION_INVALID: EmailCodeConfirmationInvalid,
  EMAIL_ALREADY_CONFIRMED: EmailAlreadyConfirmed,
  EMAIL_CONFIRMATION_COOLDOWN: EmailConfirmationCooldown,
  EMAIL_CONFIRMATION_EXPIRED: EmailConfirmationExpired,
  TOKEN_EXPIRED_ERROR: TokenExpiredError,
  MAX_ATTEMPTS_REACH: MaxAttemptsReach,
  TWO_STEP_REQUIRED: TwoStepRequiredError,
  AWAIT_RETRY_ERROR: AwaitRetryError,
  // Todo: Create the missing errors for the following:
  TOKEN_INVALID_ERROR: UnknownError,
  INVALID_TOKEN: UnknownError,
  FORBIDDEN_ERROR: UnknownError,
  INVALID_PASSWORD_ERROR: UnknownError,
  EMAIL_SEND_ERROR: UnknownError,
  CODE_INVALID_ERROR: UnknownError,
  CODE_EXPIRED_ERROR: UnknownError,
  HTTP_CLIENT_ERROR: UnknownError,
  FORM_ERROR: UnknownError,
  JSON_MALFORMED_ERROR: UnknownError,
  SEND_SMS_ERROR: UnknownError,
  DATABASE_ERROR: UnknownError,
  UNDER_MAINTENANCE_ERROR: UnknownError,
  BACK_ACCOUNT_ERROR: UnknownError,
  WISH_FC_SHOP_IDS_ERROR: UnknownError,
  GENERATE_TEMPLATE_ERROR: UnknownError,
  GET_TEMPLATE_ERROR: UnknownError,
};

axios.defaults.baseURL = import.meta.env.VITE_API_URL;

const sensitiveRoutes = ['/customers/auth/'];

const isAuthError = (ErrorClass: any): ErrorClass is typeof UnauthorizedError | typeof TokenExpiredError => {
  return ErrorClass === UnauthorizedError || ErrorClass === TokenExpiredError;
};

const handleAuthError = (message: string, ErrorClass: new (msg: string, data?: unknown) => Error, data?: unknown) => {
  const authStore = useAuthStore();
  authStore.removeUser();
  authStore.removeToken();
  localStorage.clear();
  router.push({ name: 'Login' });
  throw new ErrorClass(message, data);
};

axios.interceptors.response.use(
  (res) => res,
  (error: AxiosError) => {
    if (error.response?.data) {
      const data = error.response.data as { type: ErrorTypes; message: string; data: unknown };
      const ErrorClass = ErrorMap[data.type] || UnknownError;
      if (isAuthError(ErrorClass)) {
        handleAuthError(data.message, ErrorClass, data.type);
      }
      throw new ErrorClass(data.message, data.type);
    }
    const isSensitiveRoute = sensitiveRoutes.some((route) => error.config?.url?.includes(route));
    switch (error.response?.status) {
      case 401:
      case 403:
        handleAuthError(error.message, UnauthorizedError);
        break;
      case 404:
        if (isSensitiveRoute) {
          handleAuthError(error.message, UnauthorizedError);
        } else {
          throw new NotFoundError(error.message, error);
        }
        break;
      case 500:
      case 502:
      case 504:
        if (isSensitiveRoute) {
          handleAuthError(error.message, UnauthorizedError);
        } else {
          throw new UnknownError();
        }
        break;
      default:
        throw new UnknownError();
    }
    //added to handle errors from another services
    if((error.code || error.status) && isSensitiveRoute){
      const logoutCodes = ["401", "403", "404", "500", "502", "504"];
      if((error.code && logoutCodes.includes(error.code)) || (error.status && logoutCodes.includes(error.status.toString()))){
        handleAuthError(error.message, UnauthorizedError);
      }
    }
  },
);

// eslint-disable-next-line import/prefer-default-export
export const DoNetworkAPI = axios.create({
  baseURL: import.meta.env.VITE_APP_DO_NETWORK_URL,
});

export const DoNetworkDs2API = axios.create({
  baseURL: import.meta.env.VITE_APP_DS2_DO_NETWORK_URL,
});
